import { toast } from "vue3-toastify";

// Tamanho máximo de um número E.164 (DDI + assinante).
const MAX_E164_DIGITS = 15;

export function standardizePhone(phone: string, options: { throwError: boolean, returnToastError: boolean, returnErrorObject: boolean } = { throwError: false, returnToastError: true, returnErrorObject: false }) {
    const errorFunc = (message: string) => {
        if (options.throwError)
            throw new Error(message);
        else if (options.returnErrorObject)
            return new Error(message)
        else if (options.returnToastError)
            return toast.error(message)
        return false;
    }

    let standardizedPhone = phone.replaceAll(/\D/g,'');
    if (standardizedPhone.length < 10) return false && errorFunc("O número deve conter, ao menos, um DDD e oito números.")

    // Só assume DDI 55 quando o número veio sem DDI (DDD + 8 ou 9 dígitos).
    // Números com 12+ dígitos já trazem o próprio DDI (ex.: 966530495579 — Arábia Saudita).
    if (standardizedPhone.length <= 11 && !standardizedPhone.startsWith('55')) standardizedPhone = '55' + standardizedPhone; // 55 35 ? 1111 2222

    // 9º dígito é regra brasileira — nunca aplicar a outros DDIs.
    if (standardizedPhone.startsWith('55') && standardizedPhone.length == 12) standardizedPhone = standardizedPhone.slice(0,4) + '9' + standardizedPhone.slice(4, Infinity); // 55 35 9 1111 2222

    if (standardizedPhone.length > MAX_E164_DIGITS) return false && errorFunc("O número inserido é anormalmente grande.")

    return standardizedPhone;
}

// funcao alinhada com o béck
// a vida é dura
export function normalizarPhone(phone: string) {
  return normalizarPhoneBR(phone) ?? phone.replace(/\D/g, '');
}

/** Miolo do `normalizarPhone`: forma canônica BR, ou `null` se não é número brasileiro válido. */
export function normalizarPhoneBR(phone: string): string | null {
  let digits = phone.replace(/\D/g, '');

  if (/^015\d{2}\d{8,9}$/.test(digits)) {
    digits = digits.replace(/^015/, '55');
  }

  if (!/^55\d{10,11}$/.test(digits)) {
    return null;
  }

  if (digits.length === 12) {
    const localStart = digits[4];
    if ('6789'.includes(localStart)) {
      digits = digits.slice(0, 4) + '9' + digits.slice(4);
    }
  }

  if (!/^55\d{2}(?:9\d{8}|[2-5]\d{7})$/.test(digits)) {
    return null;
  }

  return digits;
}

export function formatPhone(phone: string) {
    if (!phone) return phone;

    // Sem DDI 55 não existe regra de 9º dígito: só prefixa o "+" quando a entrada
    // é um número puro (evita mexer em JID de grupo, @lid, texto etc.).
    if (!phone.startsWith('55')) return /^\d{8,15}$/.test(phone) ? `+${phone}` : phone;

    const fmtCom9 = phone.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '+$1 ($2) $3-$4');
    if (fmtCom9 != phone) return fmtCom9;
    else return phone.replace(/(\d{2})(\d{2})(\d{4})(\d{4})/, '+$1 ($2) 9$3-$4');
}

/**
 * Número que chegou sem DDI e PODERIA ser brasileiro: com o 55 na frente, o
 * normalizador aceita. Devolve a forma canônica com 55, ou `null`.
 *
 * Não dá para prefixar o 55 sozinho: vários DDIs coincidem com DDDs daqui
 * (1 EUA/Canadá, 44 Reino Unido, 49 Alemanha, 61 Austrália...), então os
 * mesmos dígitos podem ser daqui ou de fora. Quem decide é o usuário.
 */
export function numeroSemDdiAmbiguo(phone: string): string | null {
    return normalizarPhoneBR(`55${phone.replace(/\D/g, '')}`);
}

/**
 * Mais dígitos do que qualquer telefone pode ter: o E.164 limita o número completo
 * (com código do país) a 15. Um código assim não é telefone, então não tem
 * WhatsApp — e o WhatsApp nem responde por ele (a consulta volta sem a entrada e
 * vira "desconhecido"). LIDs têm 14–15 dígitos e ficam abaixo do limite.
 * Endereços (`@lid`, `@g.us`) não são avaliados, e o prefixo internacional `00`
 * não conta (o E.164 não o inclui).
 *
 * SÓ para código de WhatsApp: IGSID (Instagram) e PSID (Messenger) também moram em
 * `codigo` e têm 16–17 dígitos — quem chama filtra o canal.
 */
export function excedeTamanhoDeTelefone(codigo: string | null | undefined): boolean {
    if (!codigo || codigo.includes('@')) return false;
    return codigo.replace(/\D/g, '').replace(/^00/, '').length > MAX_E164_DIGITS;
}

/**
 * O código é o LID do próprio contato (contato só-LID guarda os dígitos do LID em
 * `codigo`) — espelha o `codigoEhLid` do servidor. LID não é telefone, então a
 * regra de tamanho não vale para ele. `lid` vem do servidor como `<dígitos>@lid`.
 */
export function codigoEhLidDoContato(codigo: string | null | undefined, lid: string | null | undefined): boolean {
    if (!codigo) return false;
    if (codigo.includes('@lid')) return true;
    const dc = codigo.replace(/\D/g, '');
    return !!lid && dc.length > 0 && dc === lid.replace(/\D/g, '');
}

/** DDIs que existem — o mesmo conjunto que o servidor usa na importação (libphonenumber-js). */
export const DDIS_EXISTENTES = new Set('1,7,20,27,30,31,32,33,34,36,39,40,41,43,44,45,46,47,48,49,51,52,53,54,55,56,57,58,60,61,62,63,64,65,66,81,82,84,86,90,91,92,93,94,95,98,211,212,213,216,218,220,221,222,223,224,225,226,227,228,229,230,231,232,233,234,235,236,237,238,239,240,241,242,243,244,245,246,247,248,249,250,251,252,253,254,255,256,257,258,260,261,262,263,264,265,266,267,268,269,290,291,297,298,299,350,351,352,353,354,355,356,357,358,359,370,371,372,373,374,375,376,377,378,380,381,382,383,385,386,387,389,420,421,423,500,501,502,503,504,505,506,507,508,509,590,591,592,593,594,595,596,597,598,599,670,672,673,674,675,676,677,678,679,680,681,682,683,685,686,687,688,689,690,691,692,850,852,853,855,856,880,886,960,961,962,963,964,965,966,967,968,970,971,972,973,974,975,976,977,992,993,994,995,996,998'.split(','));
